import { ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material';
import { MaintenancesApi } from '@api/maintenances/maintenances.api';
import { Maintenance, MaintenanceProductServicePageResult } from '@core/interfaces';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { isNil } from 'lodash';
import { debounceTime, first } from 'rxjs/operators';
import { sentry } from 'sentry';
import { AuthenticationService } from '../../../../../../core/authentication/authentication.service';
import english from '../../../../i18n/en.json';
import spanish from '../../../../i18n/es.json';
import portuguese from '../../../../i18n/pt.json';
import { MaintenanceDialogData } from '../../dialog-maintenances.component';

@Component({
  selector: 'tab-services',
  templateUrl: './tab-services.component.html',
  styleUrls: ['./tab-services.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TabServicesComponent implements OnInit {
  public paginationLimit: number;
  public paginationOffset: number;
  public loadingServices: boolean;
  public searchInputValue: FormControl;
  public maintenance: Maintenance;
  public services: MaintenanceProductServicePageResult;
  public filteredServices: MaintenanceProductServicePageResult;
  public orderBy = 'name_asc';
  public paymentsFeatureFlag = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: MaintenanceDialogData,
    private readonly progressBar: FuseProgressBarService,
    private readonly maintenancesApiService: MaintenancesApi,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly authenticationService: AuthenticationService
  ) {
    this.services = null;
    this.filteredServices = null;
    this.paginationLimit = 0;
    this.paginationOffset = 0;
    this.loadingServices = true;
    this.searchInputValue = new FormControl();
    this.maintenance = this.data.maintenance;
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
  }

  public async ngOnInit(): Promise<void> {
    if (this.authenticationService.hasFlags('paymentsFeatureFlag')) {
      this.paymentsFeatureFlag = true;
    }
    await this.loadServices();

    this.searchInputValue
      .valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.paginationOffset = 0;
        this.loadServices();
      });
  }

  public async toNextPage(): Promise<void> {
    this.paginationOffset += 5;
    await this.loadServices();
  }

  public async toPreviousPage(): Promise<void> {
    this.paginationOffset -= 5;
    await this.loadServices();
  }

  public async orderByName(): Promise<void> {
    if (this.orderBy === 'name_asc') {
      this.orderBy = 'name_desc';
    } else {
      this.orderBy = 'name_asc';
    }

    await this.loadServices();
  }

  private async showLoading(): Promise<void> {
    this.loadingServices = true;
    const isProgressBarVisible = await this.progressBar.visible
      .pipe(first())
      .toPromise();

    if (!isProgressBarVisible) {
      this.progressBar.show();
      this.changeDetectorRef.markForCheck();
    }
  }

  private async hideLoading(): Promise<void> {
    this.loadingServices = false;
    const isProgressBarVisible = await this.progressBar.visible
      .pipe(first())
      .toPromise();

    if (isProgressBarVisible) {
      this.progressBar.hide();
      this.changeDetectorRef.markForCheck();
    }
  }

  private async loadServices(): Promise<void> {
    this.showLoading();

    if (isNil(this.data.maintenance.company && this.data.maintenance.company.id)) {
      await this.hideLoading();
      return;
    }

    const variables: Record<string, any> = {
      limit: 5,
      offset: this.paginationOffset,
      orderBy: this.orderBy,
      maintenanceId: this.data.maintenance.id,
      where: {
        type: 'service'
      }
    };

    try {
      this.services = await this.maintenancesApiService
        .searchMaintenanceProductsServices(variables)
        .toPromise();

      if (this.services.totalCount === null) {
        sentry.captureMessage('Error loading services', {
          extra: {
            variables: JSON.stringify(variables)
          },
          user: {
            id: this.authenticationService.user.id,
            email: this.authenticationService.user.email,
            username: this.authenticationService.user.name,
          }
        });
      }

      const hasInputValue = Boolean(this.searchInputValue.value);
      if (hasInputValue) {
        variables.where = {
          name_contains: this.searchInputValue.value
        };
      }

      this.filteredServices = hasInputValue
        ? await this.maintenancesApiService.searchMaintenanceProductsServices(variables).toPromise()
        : this.services;

      this.paginationLimit = this.filteredServices && this.filteredServices.totalCount <= 5
        ? this.filteredServices.totalCount
        : 5;
    } catch (err) {
      const user = this.authenticationService.user;
      sentry.captureException(err, {
        extra: {
          variables: JSON.stringify(variables)
        },
        user: {
          id: user.id,
          email: user.email,
          username: user.name,
        }
      });
    } finally {
      this.hideLoading();
    }
  }
}
